/**
 * @fileoverview Утилиты для работы с URL-кодированием
 *
 * Этот модуль содержит функции для безопасной обработки URL в веб-приложении.
 * Основная проблема, которую решает этот модуль - некорректное отображение изображений
 * и файлов, URL которых содержат специальные символы, особенно квадратные скобки.
 *
 */

/**
 * Рекурсивно декодирует URL-кодированную строку до тех пор, пока не останется больше закодированных символов.
 *
 * Эта функция решает проблему множественного кодирования URL, когда строка была закодирована несколько раз.
 * Например: "Hello%2520World" -> "Hello%20World" -> "Hello World"
 *
 * @param link - URL-кодированная строка для декодирования
 * @returns Полностью декодированная строка или исходная строка в случае ошибки
 *
 * @example
 * recursiveDecode("Hello%2520World") // "Hello World"
 * recursiveDecode("https%3A%2F%2Fexample.com") // "https://example.com"
 * recursiveDecode("already-decoded") // "already-decoded"
 *
 * @useCase
 * Используется при открытии файлов в новом окне через window.open(link, '_blank').
 * В компоненте Gallery эта функция применяется для декодирования URL перед открытием
 * документов (APPLICATION) в новой вкладке браузера.
 */
export const recursiveDecode = (link: string): string => {
  try {
    const decodedLink = decodeURIComponent(link);
    return decodedLink === link ? link : recursiveDecode(decodedLink);
  } catch (e) {
    return link;
  }
};

/**
 * Подготавливает URL для безопасного использования в веб-приложении.
 *
 * Эта функция решает проблему с некорректным отображением изображений и файлов,
 * URL которых содержат специальные символы, особенно квадратные скобки.
 *
 * Проблема: стандартная функция encodeURI() кодирует квадратные скобки [ ] в %5B %5D,
 * что может вызывать проблемы на некоторых веб-серверах и CDN.
 *
 * Решение: сначала полностью декодируем URL, затем кодируем его заново,
 * но оставляем квадратные скобки незакодированными.
 *
 * @param link - URL для обработки (может быть уже закодированным)
 * @returns Безопасно закодированный URL с незакодированными квадратными скобками
 *
 * @example
 * getEncodedURI("https://example.com/image[1].jpg") // "https://example.com/image[1].jpg"
 * getEncodedURI("https://example.com/фото[1].jpg") // "https://example.com/%D1%84%D0%BE%D1%82%D0%BE[1].jpg"
 * getEncodedURI("https://example.com/image%5B1%5D.jpg") // "https://example.com/image[1].jpg"
 *
 * @useCase
 * Используется в компонентах Gallery, Upload и других местах для обработки URL медиафайлов
 * перед их отображением в <img src=""> или передачей в API запросы.
 */
export const getEncodedURI = (link: string) => {
  const decodedLink = recursiveDecode(link);

  // Сначала кодируем URL полностью
  const encoded = encodeURI(decodedLink);

  // Затем декодируем квадратные скобки обратно, так как они могут вызывать проблемы на сервере
  return encoded.replace(/%5B/g, '[').replace(/%5D/g, ']');
};
